import { useCallback, useEffect, useMemo, useRef } from 'react'
import { reportStationCardEvent } from '@metro/station-card'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'

/**
 * Host-side analytics wiring for the compact (phone) `<StationCard>`
 * carousel. Mirrors {@link useStationDetailsAnalytics}: it wraps the
 * host's card callbacks so each user action ALSO emits its cross-platform
 * `station_card_*` event through the project {@link MetroAnalyticsReporter},
 * and fires `_opened` once per resolved-station transition + `_closed` once
 * per dismissal.
 *
 * Why a host-side hook (not inside the package): `@metro/station-card`
 * is intentionally stateless + domain-agnostic — it exports the typed
 * facade `reportStationCardEvent` but never imports an analytics backend.
 * Wiring is the host's job (same pattern as StationDetails / Suggester /
 * Routing). Pre-this-task the facade had ZERO call sites in the host —
 * every `station_card_*` event was dark at runtime on web while iOS +
 * Android emitted them.
 *
 * Cross-platform contract (byte-identical with iOS / Android), from
 * `@metro/station-card` `StationCardAnalytics.ts`:
 *   station_card_opened          { station_id, transfers_count }
 *   station_card_settled         { station_id }
 *   station_card_from_tapped     { station_id }
 *   station_card_to_tapped       { station_id }
 *   station_card_details_tapped  { station_id }
 *   station_card_transfer_tapped { from_id, to_id }
 *   station_card_closed          { station_id }
 *
 * `station_card_transfer_tapped` fires on a settle-to-transfer: when
 * the carousel settles onto a station OTHER than the originally-opened
 * primary, the swipe is a transfer interaction. iOS owns the trigger in
 * its `.stationSettled` reducer (StationCardAnalytics.swift) — emitting
 * `_settled` AND, when `settled id != origin id`, `_transfer_tapped
 * { from_id: <origin>, to_id: <settled> }`. Web mirrors that in the
 * `settled` wrapper below: `from_id` is the opened/origin station
 * (`openedIdRef`, the id `_opened` last fired for), `to_id` is the
 * settled id. (No transfer-ROW trigger exists on web — the card surface
 * renders no rows — but the settle-to-transfer trigger is the real
 * cross-platform source of this event.)
 *
 * Lifecycle (`active`): the host passes `active = !isExpanded && stationId !== null`,
 * i.e. the carousel is actually rendered (phone viewport, station selected).
 * `_opened` fires when `active` is true and `stationId` transitions to a new
 * value; `_closed` fires when an opened station leaves (`active` → false or
 * `stationId` → a different id / null), carrying the id that was open. The
 * fire-once latch mirrors the `_opened` logic in `useStationDetailsAnalytics`.
 */
export interface StationCardAnalyticsInput {
  /**
   * Currently-resolved station id driving the carousel (null = no
   * station). `_opened` / `_closed` track transitions of this id.
   */
  readonly stationId: string | null
  /**
   * Whether the compact carousel surface is actually rendered (phone
   * viewport + a station selected). When false the lifecycle events do
   * not fire — the tablet sidebar uses StationDetails analytics instead.
   */
  readonly active: boolean
  /**
   * Transfer-station count for the open station — emitted as
   * `transfers_count` on `_opened` (matches iOS / Android).
   */
  readonly transfersCount: number
  /** Project-level reporter (host injects `realMetroAnalyticsReporter`). */
  readonly reporter: MetroAnalyticsReporter
  /** Pass-through host actions; each is invoked first, then the analytics emit fires. */
  readonly onFromTap: (stationId: string) => void
  readonly onToTap: (stationId: string) => void
  readonly onDetailsTap: (stationId: string) => void
  readonly onSettled: (stationId: string) => void
}

export interface StationCardAnalyticsWiring {
  readonly onFromTap: (stationId: string) => void
  readonly onToTap: (stationId: string) => void
  readonly onDetailsTap: (stationId: string) => void
  readonly onSettled: (stationId: string) => void
}

export function useStationCardAnalytics(
  input: StationCardAnalyticsInput,
): StationCardAnalyticsWiring {
  const {
    stationId,
    active,
    transfersCount,
    reporter,
    onFromTap,
    onToTap,
    onDetailsTap,
    onSettled,
  } = input

  // Adapter: facade emits `{name, parameters}`; the project reporter is
  // `(name, metadata) => void`. Stable per-reporter so the memoised
  // wrappers stay referentially stable across renders.
  const facadeReporter = useMemo(
    () => (evt: { name: string; parameters: Record<string, string | number | boolean> }) =>
      reporter(evt.name, evt.parameters),
    [reporter],
  )

  // `_opened` once per (active) station, `_closed` when it leaves. The
  // ref tracks the id we last emitted `_opened` for so we can pair it
  // with the matching `_closed` even when `stationId` flips to null /
  // a different id (transfer-tap reuses the carousel for a new station).
  // Pattern mirrors the `_opened` fire-once latch in
  // `useStationDetailsAnalytics`, extended with a close emission.
  const openedIdRef = useRef<string | null>(null)
  useEffect(() => {
    const openId = active ? stationId : null
    if (openId === openedIdRef.current) return
    // Close the previously-open station, if any.
    if (openedIdRef.current !== null) {
      reportStationCardEvent(facadeReporter, { kind: 'closed', stationId: openedIdRef.current })
    }
    // Open the new station, if any.
    if (openId !== null) {
      reportStationCardEvent(facadeReporter, {
        kind: 'opened',
        stationId: openId,
        transfersCount,
      })
    }
    openedIdRef.current = openId
    // `transfersCount` is intentionally NOT a dep — it is read at open
    // time only; a count change without an id change must not re-fire
    // `_opened` (the station is already open). It tracks `stationId`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, active, facadeReporter])

  const fromTap = useCallback(
    (id: string): void => {
      onFromTap(id)
      reportStationCardEvent(facadeReporter, { kind: 'fromTapped', stationId: id })
    },
    [onFromTap, facadeReporter],
  )

  const toTap = useCallback(
    (id: string): void => {
      onToTap(id)
      reportStationCardEvent(facadeReporter, { kind: 'toTapped', stationId: id })
    },
    [onToTap, facadeReporter],
  )

  const detailsTap = useCallback(
    (id: string): void => {
      onDetailsTap(id)
      reportStationCardEvent(facadeReporter, { kind: 'detailsTapped', stationId: id })
    },
    [onDetailsTap, facadeReporter],
  )

  const settled = useCallback(
    (id: string): void => {
      onSettled(id)
      reportStationCardEvent(facadeReporter, { kind: 'settled', stationId: id })
      // Settle-to-transfer: settling onto a station other than the
      // originally-opened primary is a transfer interaction. Mirrors iOS
      // `.stationSettled` — `_transfer_tapped { from_id: origin, to_id:
      // settled }` ALONGSIDE `_settled`. `openedIdRef.current` is the
      // origin (the id `_opened` last fired for).
      const originId = openedIdRef.current
      if (originId !== null && originId !== id) {
        reportStationCardEvent(facadeReporter, {
          kind: 'transferTapped',
          fromId: originId,
          toId: id,
        })
      }
    },
    [onSettled, facadeReporter],
  )

  return { onFromTap: fromTap, onToTap: toTap, onDetailsTap: detailsTap, onSettled: settled }
}
